import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { formatDateTime, cn } from '../../lib/utils';
import { Search, History, User, FileText, Calendar, AlertTriangle, Home, Shield } from 'lucide-react';
import { toast } from 'sonner';

const AuditLogsPage = () => {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [entityFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = entityFilter !== 'all' ? { entity_type: entityFilter } : {};
      const res = await api.get('/audit-logs', { params: { ...params, limit: 200 } });
      setLogs(res.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const entityIcons = {
    user: User,
    shift: Calendar,
    incident: AlertTriangle,
    participant: User,
    house: Home,
    compliance_rule: Shield,
    leave_request: Calendar,
    shift_note: FileText,
    diary_post: FileText
  };

  const actionColors = {
    created: 'bg-green-500/10 text-green-500 border-green-500/30',
    updated: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
    deleted: 'bg-red-500/10 text-red-500 border-red-500/30',
    status_changed: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
    response: 'bg-purple-500/10 text-purple-500 border-purple-500/30'
  };

  const getActionColor = (action) => {
    for (const [key, value] of Object.entries(actionColors)) {
      if (action.toLowerCase().includes(key)) return value;
    }
    return 'bg-slate-500/10 text-slate-500 border-slate-500/30';
  };

  const filteredLogs = logs.filter(l => 
    l.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.entity_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      header: 'Timestamp',
      cell: (row) => (
        <span className="text-sm font-mono">{formatDateTime(row.timestamp)}</span>
      )
    },
    {
      header: 'User',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
            {row.user_name?.split(' ').map(n => n[0]).join('') || '?'}
          </div>
          <span className="text-sm">{row.user_name || 'System'}</span>
        </div>
      )
    },
    {
      header: 'Action',
      cell: (row) => (
        <Badge variant="outline" className={cn("font-medium", getActionColor(row.action))}>
          {row.action.replace(/_/g, ' ')}
        </Badge>
      )
    },
    {
      header: 'Entity',
      cell: (row) => {
        const Icon = entityIcons[row.entity_type] || FileText;
        return (
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm capitalize">{row.entity_type.replace(/_/g, ' ')}</span>
          </div>
        );
      }
    },
    {
      header: 'Entity ID',
      cell: (row) => (
        <span className="text-xs font-mono text-muted-foreground">
          {row.entity_id ? row.entity_id.substring(0, 8) + "..." : "N/A"}
        </span>
      )
    },
    {
      header: 'Changes',
      cell: (row) => {
        const changes = row.changes || {};
        const keys = Object.keys(changes).slice(0, 3);
        return (
          <div className="text-xs text-muted-foreground">
            {keys.length > 0 ? (
              keys.map(key => (
                <span key={key} className="mr-2">
                  <span className="font-medium">{key}:</span> {String(changes[key]).substring(0, 20)}
                </span>
              ))
            ) : '—'}
          </div>
        );
      }
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-logs-page">
      <PageHeader 
        title="Audit Logs"
        description="View system activity and changes for compliance"
      />

      {/* Filters */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Entity Type</Label>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-48" data-testid="audit-entity-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="user">Users</SelectItem>
                  <SelectItem value="shift">Shifts</SelectItem>
                  <SelectItem value="incident">Incidents</SelectItem>
                  <SelectItem value="participant">Participants</SelectItem>
                  <SelectItem value="house">Houses</SelectItem>
                  <SelectItem value="compliance_rule">Compliance Rules</SelectItem>
                  <SelectItem value="leave_request">Leave Requests</SelectItem>
                  <SelectItem value="shift_note">Shift Notes</SelectItem>
                  <SelectItem value="diary_post">Diary Posts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1 max-w-md">
              <Label className="text-xs text-muted-foreground">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search by user, action, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="audit-search"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <DataTable 
        columns={columns}
        data={filteredLogs}
        emptyMessage="No audit logs found"
      />
    </div>
  );
};

export default AuditLogsPage;
